import type { ConsignmentDirection, ConsignmentEntryView } from '@cuito/types';
import { AMOUNT_UNAVAILABLE_TEXT, formatServerYuan } from '../../lib/schemeForm';

/**
 * 台账条目表（契约 §14.3 的 `entries`；UI 规范 550 行：「点行展开**条目**
 * （`CREDIT` / `DEBIT` / `REVERSE` 三向徽标 + 金额 / 佣金 / 净额 / 关联订单号 / 时间）」）。
 *
 * ## 三向徽标的文案（**本文件是唯一出处**）
 *
 * UI 规范那一行逐字写的是**枚举本身**（`CREDIT` / `DEBIT` / `REVERSE`），而冻结文档 §五 的
 * 「中文文案」表里**没有**这三个方向的词 ⇒ 本文件按 UI 规范显示枚举 token，
 * 「是否要中文（分账 / 冲减 / 反向）」作为**待裁决 D-3** 提交。
 * 未知取值**显示原文**（不 `undefined`）—— 与 `refundLabels.ts` 的守卫同款。
 *
 * ## 金额一律字符串透传
 *
 * `amount` / `commission` / `net` 是条目上快照好的服务端字段，前端**不做**任何减法：
 * 净额 = 金额 − 佣金 是**写条目时**算好的（§14.0），前端再算一次就是第二份口径。
 * 展示形态沿用 `formatServerYuan`（D-16 裁决：与 `LedgerSummaryCards` 同款，加 `¥` 与千分位）；
 * 读不出来的字段说 `金额以服务端为准`，**绝不**说 `¥0.00`。
 *
 * ## `consignment-entries` 挂在**容器**上（不是 `<table>` 上）
 *
 * 冻结 testid 的语义是「条目容器」：加载态也要有它（`ConsignmentTable.spec.tsx` ③ 断言展开行
 * 渲染出容器**且**里面是 `加载中…`）。挂在 `<table>` 上会让加载态/错误态/空态都没有这个节点，
 * 于是「未展开」与「展开但还在加载」在 DOM 上无法区分。
 */
export const CONSIGNMENT_DIRECTION_TEXT: Record<ConsignmentDirection, string> = {
  CREDIT: 'CREDIT',
  DEBIT: 'DEBIT',
  REVERSE: 'REVERSE',
};

export const CONSIGNMENT_DIRECTION_CLASS: Record<ConsignmentDirection, string> = {
  CREDIT: 'border-black bg-white text-black',
  DEBIT: 'border-black bg-gray-100 text-gray-700',
  REVERSE: 'border-black bg-black text-white',
};

function isDirection(value: string): value is ConsignmentDirection {
  return value === 'CREDIT' || value === 'DEBIT' || value === 'REVERSE';
}

export function consignmentDirectionText(direction: string): string {
  return isDirection(direction) ? CONSIGNMENT_DIRECTION_TEXT[direction] : direction;
}

export function consignmentDirectionClass(direction: string): string {
  return isDirection(direction)
    ? CONSIGNMENT_DIRECTION_CLASS[direction]
    : 'border-gray-400 bg-white text-gray-500';
}

interface Props {
  entries: ConsignmentEntryView[];
  loading: boolean;
  error: string | null;
}

export default function ConsignmentEntriesTable({ entries, loading, error }: Props) {
  if (loading) {
    return (
      <div data-testid="consignment-entries">
        <p className="py-4 text-center text-gray-500">加载中…</p>
      </div>
    );
  }
  if (error !== null) {
    return (
      <div data-testid="consignment-entries">
        <p className="py-4 font-bold text-red-600">{error}</p>
      </div>
    );
  }
  if (entries.length === 0) {
    return (
      <div data-testid="consignment-entries">
        <p className="py-4 text-gray-500">暂无待结算明细</p>
      </div>
    );
  }

  return (
    <div data-testid="consignment-entries">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500">
            <th className="py-1">方向</th>
            <th className="py-1">数量</th>
            <th className="py-1">金额</th>
            <th className="py-1">佣金</th>
            <th className="py-1">净额</th>
            <th className="py-1">关联订单号</th>
            <th className="py-1">时间</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr
              key={entry.id}
              className="border-t border-gray-200"
              data-testid="consignment-entry-row"
              data-direction={entry.direction}
            >
              <td className="py-1">
                <span
                  data-testid="consignment-entry-direction"
                  className={`rounded border px-1.5 py-0.5 text-xs font-bold ${consignmentDirectionClass(entry.direction)}`}
                >
                  {consignmentDirectionText(entry.direction)}
                </span>
              </td>
              <td className="py-1">{entry.quantity}</td>
              <td className="py-1" data-testid="consignment-entry-amount">
                {formatServerYuan(entry.amount) ?? AMOUNT_UNAVAILABLE_TEXT}
              </td>
              <td className="py-1">{formatServerYuan(entry.commission) ?? AMOUNT_UNAVAILABLE_TEXT}</td>
              <td className="py-1">{formatServerYuan(entry.net) ?? AMOUNT_UNAVAILABLE_TEXT}</td>
              <td className="py-1">{entry.orderNo ?? '—'}</td>
              <td className="py-1">{entry.createdAt}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
